import { memo, useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Url } from '@rossum/api-client';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import {
  ExpandableSearch,
  SEARCH_MIN_CHAR_LENGTH,
} from '@rossum/components/expandable-search/ExpandableSearch';
import { Box, Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridFilterItem,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from '@rossum/ui/x-data-grid-pro';
import { useDataGridLocaleText } from '../../i18n/useDataGridLocaleText';
import { canManageQueueSelector } from '../../redux/modules/user/selectors';
import { State } from '../../types/state';
import {
  allowedPageSizesEmails,
  DEFAULT_PAGE_SIZE,
  useRowCount,
} from '../document-list-base/hooks/usePagination';
import { LevelOptions, ViewOptions } from '../document-list-base/types';
import { FilterPanel } from '../document-list-filtering/FilterPanel';
import { absoluteMaxSizeStyles } from '../document-upload/components/helpers';
import { DocumentEmailDrawer } from '../document/DocumentEmailDrawer';
import { useWorkspacesWithQueues } from '../queues/hooks/useWorkspacesWithQueues';
import { BreadCrumbs } from './components/BreadCrumbs';
import { EmptyStates } from './components/EmptyStates';
import { QueueDetails } from './components/QueueDetails';
import { SearchResults } from './components/SearchResults';
import { ToggleSidebarButton } from './components/ToggleSidebarButton';
import { ViewSwitch } from './components/ViewSwitch';
import {
  contentWrapperStyles,
  navigationPanelInnerStyles,
  navigationPanelOuterStyles,
  toolbarStyles,
} from './constants';
import { SettingsButton } from './cta-actions/SettingsButton';
import { TransformedEmailThread } from './emails/helpers';
import { useEmailsColumns } from './emails/hooks/useEmailsColumns';
import { useFetchEmailThreads } from './emails/hooks/useFetchEmailThreads';
import { useFetchEmailThreadsCounts } from './emails/hooks/useFetchEmailThreadsCounts';
import { getActiveQueue, getPageSizeFromQuery } from './helpers';
import { DashboardQuery } from './hooks/useDashboardQuery';
import { useSafelyParsedFiltering } from './hooks/useSafelyParsedFiltering';
import { useTimeoutSearchQuery } from './hooks/useTimeoutSearchQuery';

type DrawerState = {
  emailThreadUrl: Url;
  queueUrl: Url;
};

type Props = {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  view: ViewOptions;
  setView: (view: ViewOptions, options?: { replaceHistory?: boolean }) => void;
  level: LevelOptions | null;
  activeQueueId: number | undefined;
  onPaginationChange: (newPaginationModel: GridPaginationModel) => void;
  query: DashboardQuery;
  sortingProps: {
    onSortModelChange: (sortModel: GridSortModel) => void;
    sortModel: GridSortModel;
  };
  onSearchSubmit: (searchValue: string | undefined) => void;
  handleFilterModelChange: (newFilterItems: GridFilterItem[]) => void;
  handleClearAllFilters: () => void;
  isSidebarEnabled: boolean;
};

const EmailsContent = memo(
  ({
    activeQueueId,
    isSidebarOpen,
    toggleSidebar,
    view,
    setView,
    level,
    onPaginationChange,
    query,
    sortingProps: { onSortModelChange, sortModel },
    onSearchSubmit,
    handleFilterModelChange,
    handleClearAllFilters,
    isSidebarEnabled,
  }: Props) => {
    const intl = useIntl();
    const [drawerState, setDrawerState] = useState<DrawerState | undefined>(
      undefined
    );
    const [searchState, setSearchState] = useState(query['search'] ?? '');

    const { queues } = useWorkspacesWithQueues({
      enableQueries: true,
    });

    const activeQueue = getActiveQueue({
      level: query['level'],
      queues,
      queueId: activeQueueId,
    });

    const canManageActiveQueue = useSelector((state: State) =>
      canManageQueueSelector(state, activeQueue?.url)
    );

    useEffect(() => {
      // when queue has no inbox emails view are not available; replace the
      // history entry so back/forward navigation cannot return to it
      if (activeQueue && !activeQueue.inbox) {
        setView('documents', { replaceHistory: true });
      }
    }, [activeQueue, setView]);

    const { validFilters, invalidFilters } = useSafelyParsedFiltering(
      query['filtering'] ?? ''
    );

    const { data: emailThreads, isLoading: emailThreadsAreLoading } =
      useFetchEmailThreads(
        {
          queue: activeQueueId,
          hasRootEmail: true,
          page: query['page'] ? parseInt(query['page'], 10) : 1,
          pageSize: getPageSizeFromQuery({
            query,
            fallbackValue: DEFAULT_PAGE_SIZE,
            allowedPageSizes: allowedPageSizesEmails,
          }),
          ...(query['search'] ? { search: query['search'] } : {}),
        },
        validFilters,
        query['ordering'] ?? ''
      );

    const { data: counts } = useFetchEmailThreadsCounts({
      queueId: activeQueueId,
    });

    const debouncedSearching = useMemo(
      () => debounce(onSearchSubmit, 400),
      [onSearchSubmit]
    );

    const localeText = useDataGridLocaleText();

    const columns = useEmailsColumns(counts);

    const rowCount = useRowCount(emailThreads?.pagination.total);

    const paginationModel: GridPaginationModel = useMemo(
      () => ({
        page: !query['page'] ? 0 : parseInt(query['page'], 10) - 1,
        pageSize: getPageSizeFromQuery({
          query,
          fallbackValue: DEFAULT_PAGE_SIZE,
          allowedPageSizes: allowedPageSizesEmails,
        }),
      }),
      [query]
    );

    const filterableColumns = useMemo(
      () => columns.flatMap(column => (column.filterable ? [column] : [])),
      [columns]
    );

    const search = useTimeoutSearchQuery(query['search']);

    return (
      <Stack {...contentWrapperStyles} data-page-title="email-list">
        <Stack {...navigationPanelOuterStyles}>
          <Stack {...navigationPanelInnerStyles}>
            {isSidebarEnabled && (
              <ToggleSidebarButton
                isSidebarOpen={isSidebarOpen}
                onClick={toggleSidebar}
              />
            )}
            <BreadCrumbs
              activeQueueId={activeQueueId}
              allDocumentsLevelIsActive={level === 'all'}
            />
            <ViewSwitch
              isVisible={level === 'queue'}
              view={view}
              setView={setView}
              counts={counts}
            />
            {activeQueueId && canManageActiveQueue ? (
              <SettingsButton queueId={activeQueueId} view="emails" />
            ) : null}
            {activeQueue ? <QueueDetails activeQueue={activeQueue} /> : null}
          </Stack>
          <Stack direction="row">
            <Stack {...toolbarStyles}>
              <ExpandableSearch
                onChange={newSearch => {
                  setSearchState(newSearch);
                  debouncedSearching(
                    newSearch.length >= SEARCH_MIN_CHAR_LENGTH
                      ? newSearch
                      : undefined
                  );
                }}
                value={searchState}
                placeholder={intl.formatMessage({
                  id: 'components.dashboard.queues.search',
                })}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack gap={2} mt={2}>
          <FilterPanel
            columns={filterableColumns}
            handleFilterModelChange={handleFilterModelChange}
            handleClearAllFilters={handleClearAllFilters}
            validFilters={validFilters}
            invalidFilters={invalidFilters}
            hasActiveQueue={!!activeQueueId}
          />
          <SearchResults querySearch={query['search']} />
        </Stack>
        <Box
          position="relative"
          flexGrow={1}
          sx={{
            backgroundColor: 'inherit',
          }}
        >
          <DataGridPro
            disableRowSelectionExcludeModel
            loading={emailThreadsAreLoading}
            columns={columns}
            columnVisibilityModel={{
              options: false, // column options exists only for filtering purposes
            }}
            rows={emailThreads?.results?.length ? emailThreads.results : []}
            localeText={localeText}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            pagination
            onRowClick={(row: GridRowParams<TransformedEmailThread>) => {
              setDrawerState({
                emailThreadUrl: row.row.url,
                queueUrl: row.row.queueUrl,
              });
            }}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationChange}
            pageSizeOptions={allowedPageSizesEmails}
            rowCount={rowCount}
            onSortModelChange={onSortModelChange}
            sortModel={sortModel}
            slots={{
              noRowsOverlay: () => (
                <EmptyStates
                  isEmailView
                  searchQuery={search}
                  queue={activeQueue}
                  existingFilterItems={validFilters}
                  onClearFilters={handleClearAllFilters}
                />
              ),
              columnHeaders: ColumnHeaderPaper,
            }}
            slotProps={{
              columnHeaders: {
                // @ts-expect-error
                elevation: 0,
              },
              loadingOverlay: {
                variant: 'linear-progress',
              },
              row: { 'data-cy': 'email-row' },
              basePagination: {
                // @ts-expect-error wrong type
                labelRowsPerPage: intl.formatMessage({
                  id: 'containers.allDocuments.pagination.label',
                }),
                'data-cy': 'emails-pagination',
                backIconButtonProps: {
                  'data-cy': 'emails-pagination-prev-btn',
                },
                nextIconButtonProps: {
                  'data-cy': 'emails-pagination-next-btn',
                },
                sx: {
                  // hide counter when there are no rows
                  display: rowCount === 0 ? 'none' : 'inherit',
                  justifyContent: 'flex-end',
                  [`> * p`]: {
                    margin: 0,
                  },
                },
              },
              baseSwitch: {
                // @ts-expect-error
                color: 'secondary',
              },
            }}
            sx={{
              '&': {
                '--DataGrid-pinnedBackground': t => t.palette.background.paper,
                '--DataGrid-containerBackground': t =>
                  t.palette.background.paper,
              },
              ...commonDataGridStyles,
              ...absoluteMaxSizeStyles,
              [`.${gridClasses.row}`]: {
                cursor: 'pointer',
              },
            }}
          />
        </Box>

        <DocumentEmailDrawer
          emailThreadUrl={drawerState ? drawerState.emailThreadUrl : null}
          queueUrl={drawerState ? drawerState.queueUrl : ''}
          onClose={() => setDrawerState(undefined)}
        />
      </Stack>
    );
  }
);

export { EmailsContent };
